import SingleLineText from './ui/SingleLineText';
import TextField from './BackAwareTextField';
import React from 'react';
import { Box, Typography, Alert, MenuItem } from '@mui/material';
import { sortAdminNewest } from '../utils/adminChronology';
const labels = { ios: 'iOS', android: 'Android', unknown: '미확인·기타' };
const series = [
    { key: 'ios', label: 'iOS', color: '#2563eb' },
    { key: 'android', label: 'Android', color: '#16a34a' },
    { key: 'unknown', label: '미확인·기타', color: '#64748b' },
];
const number = value => Number(value || 0).toLocaleString('ko-KR');

export default function AcquisitionPlatforms({ data, platform = 'all', days = 7, includeToday = true, registrationMode = 'live', onRegistrationModeChange, LineChartComponent }) {
    const rows = sortAdminNewest(data?.rows);
    const hasReport = Array.isArray(data?.rows);
    const platforms = series.filter(s => (platform === 'all' || s.key === platform) && (s.key !== 'unknown' || rows.some(r => r.unknown > 0)));
    const live = registrationMode === 'live';
    const registrationSeries = [
        { key: live ? 'registeredSoFarRate' : 'registered24Rate', label: '전체', color: '#9333ea', strokeWidth: 3.5 },
        ...platforms.map(s => ({ ...s, key: `${s.key}${live ? 'RegisteredSoFarRate' : 'Registered24Rate'}` })),
    ];
    const total = rows.reduce((sum, r) => sum + r.total, 0);
    const waiting = rows.reduce((sum, r) => sum + r.waiting24, 0);
    const unobserved = rows.reduce((sum, r) => sum + (r.unobserved24 || 0), 0);
    const subtitle = `최근 ${days}일 · ${includeToday ? '오늘 포함' : '어제까지'} · 단위: 기기 UUID`;
    return <Box sx={{ my: 3 }}>
        <Box component="section" aria-label="신규 유입 현황" sx={{ mb: 4 }}>
            <SingleLineText component="h2" sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>신규 유입은 얼마나 늘었나요?</SingleLineText>
            <Typography sx={{ fontSize: '.85rem', color: '#666', mb: 1.5 }}>서버에 처음 등록된 기기 UUID를 세요. 온보딩 중 종료하거나 약 등록을 중단해도 포함해요.</Typography>
            {platform !== 'all' && <Alert severity="info" sx={{ mb: 1.5 }}>현재 {labels[platform]}만 조회하고 있어요. 두 플랫폼을 비교하려면 조회 조건의 플랫폼을 ‘전체’로 바꿔주세요.</Alert>}
            {!hasReport ? <Typography>신규 유입 추이를 불러오지 못했어요. 상단 새로고침으로 다시 확인해주세요.</Typography> : <>
                <Typography sx={{ mb: 1.5 }}>신규 유입 총 {number(total)}기기</Typography>
                {LineChartComponent && <LineChartComponent title="운영체제별 일별 신규 유입 수" subtitle={subtitle} data={rows} series={[{ key: 'total', label: '전체', color: '#9333ea', strokeWidth: 3.5 }, ...platforms]} />}
                {total === 0 && <Typography sx={{ color: '#666', fontSize: '.85rem' }}>선택한 기간에 관측된 신규 유입이 없어요.</Typography>}
                {includeToday && <Typography sx={{ color: '#777', fontSize: '.8rem' }}>오늘은 집계 중이에요.</Typography>}
            </>}
        </Box>
        <Box component="section" aria-label="신규 유입 대비 약 등록 비율" sx={{ mb: 4 }}>
            <SingleLineText component="h2" sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>신규 유입 대비 약 등록 비율</SingleLineText>
            <Typography sx={{ fontSize: '.85rem', color: '#666', mb: 1.5 }}>기기 최초 등록 후 24시간 안에 그 기기에서 첫 약을 저장한 비율이에요.</Typography>
            {!hasReport ? <Typography>약 등록 비율 추이를 불러오지 못했어요.</Typography> : <>
                <TextField select size="small" label="등록 비율 집계" value={registrationMode} onChange={e => onRegistrationModeChange?.(e.target.value)} sx={{ minWidth: 160, mb: 2 }}>
                    <MenuItem value="live">실시간 집계</MenuItem><MenuItem value="confirmed">24시간 확정</MenuItem>
                </TextField>
                {LineChartComponent && <LineChartComponent title={live ? '유입일별 실시간 약 등록 비율' : '유입일별 24시간 내 약 등록 비율'} subtitle={`기기 최초 등록 날짜 기준 · ${live ? '현재까지 저장 성공 · 잠정 수치' : '24시간 관찰 완료 기기'} · 단위: %`} data={rows} series={registrationSeries} percent preserveMissing missingValueLabel="-" valueFormatter={value => `${value.toFixed(1)}%`} />}
                <Typography sx={{ color: '#777', fontSize: '.8rem', lineHeight: 1.7 }}>{live
                    ? `관찰 중 ${number(waiting)}기기도 포함해 현재까지의 저장 성공을 바로 반영해요. 24시간 내 첫 저장만 세며, 관찰 중인 날짜의 비율은 바뀔 수 있어요. 신규 유입이 없거나 등록 여부가 미확인이면 -로 표시해요.`
                    : `24시간이 지난 신규 기기만 분모에 포함해요. 관찰 중 ${number(waiting)}기기는 제외하며, 신규 유입이 없거나 관찰이 끝나지 않은 날짜는 -로 표시해요.`}</Typography>
                {unobserved > 0 && <Alert severity="info" sx={{ mt: 1.5 }}>등록 기록 수집 시작 이전 {number(unobserved)}기기는 신규 유입에는 포함하지만, 등록 여부 미확인으로 등록 비율에서 제외해요.</Alert>}
            </>}
        </Box>
    </Box>;
}
